import { lazy, Suspense, useEffect, useMemo } from "react";
import { ErrorBoundary, useAppContext, type NonBlockEntrypoint } from "@softr-io/blocks-sdk/react";
import type { BlockDefinition, LegacyBlockTheme } from "@softr-io/blocks-sdk";
import { useAppConstants } from "../AppConstantsContext";
import { retry } from "../retry";
import {
    clearAppBadge,
    ensurePushSubscriptionIfPermitted,
    isPushEnabledForDevice,
    PushPromptContext,
    registerServiceWorker,
    shouldRegisterServiceWorker,
    usePushPromptState,
} from "./pwa";
import { usePageContext } from "@/PageContext";
import { log } from "@/logger";

interface Props {
    definitions: ReadonlyArray<BlockDefinition>;
    theme: LegacyBlockTheme;
}

export function PwaController(props: Props) {
    const appCtx = useAppContext();
    if (!shouldRegisterServiceWorker(appCtx)) {
        return null;
    }

    return <PwaControllerImpl {...props} />;
}

function PwaControllerImpl({ definitions, theme }: Props) {
    const { baseUrl, appId, spaMode, showMadeWithBadge, badgeType } = useAppConstants();
    const baseUrlWithoutBlocks = baseUrl.replace(/\/blocks(\/)?$/, "");
    const pwaUIPath = `${baseUrlWithoutBlocks}/assets/PwaUI.mjs`;

    const PwaUI = useMemo(
        () =>
            lazy(() =>
                retry(
                    () =>
                        import(
                            /* webpackIgnore: true */
                            pwaUIPath
                        ) as Promise<{ default: NonBlockEntrypoint }>,
                ),
            ),
        [pwaUIPath],
    );

    const pushPromptView = usePushPromptState(definitions);

    useEffect(() => {
        registerServiceWorker().catch(() => {});
        // Our SW increments notification count when a push notification is received,
        // we currently don't have a way for users to clear it from the app itself
        // so we clear it on app load to avoid showing stale counts
        clearAppBadge().catch(() => {});

        // also clear when the app is refocused after being in the background,
        // since notifications may have arrived while it was hidden
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                clearAppBadge().catch(() => {});
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);

    const { push_notification_settings } = useAppContext();
    const pushEnabledForDevice =
        push_notification_settings != null && isPushEnabledForDevice(push_notification_settings);
    useEffect(() => {
        if (!pushEnabledForDevice) return;
        const ab = new AbortController();
        // subscribe immediately if the user has already granted permission before
        ensurePushSubscriptionIfPermitted(push_notification_settings, { signal: ab.signal }).catch((err) => {
            log.error("Failed to ensure push subscription:", err);
        });

        return () => {
            ab.abort();
        };
    }, [push_notification_settings, pushEnabledForDevice]);

    const { pageId } = usePageContext();
    return (
        <PushPromptContext.Provider value={pushPromptView}>
            <Suspense fallback={null}>
                <ErrorBoundary>
                    <PwaUI
                        theme={theme}
                        appId={appId}
                        pageId={pageId}
                        spaMode={spaMode}
                        baseUrlForAssetsDir={baseUrlWithoutBlocks}
                        blocks={definitions}
                        madeWithBadge={showMadeWithBadge && badgeType ? badgeType : null}
                    />
                </ErrorBoundary>
            </Suspense>
        </PushPromptContext.Provider>
    );
}
